import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaGlobe,
  FaMapMarkerAlt,
  FaBirthdayCake,
  FaPassport,
  FaGraduationCap,
  FaHome,
  FaUserFriends,
  FaHeart,
  FaCalendarAlt,
  FaChevronDown,
} from "react-icons/fa";

const ETHNIC_ORIGIN_OPTIONS = [
  { value: "", label: "Select Ethnic Origin", isHeader: false },

  // White ethnic groups
  { value: "white-header", label: "White", isHeader: true },
  {
    value: "english-welsh-scottish-northern-irish-british",
    label: "English, Welsh, Scottish, Northern Irish or British",
    isHeader: false,
  },
  { value: "irish", label: "Irish", isHeader: false },
  {
    value: "gypsy-irish-traveller",
    label: "Gypsy or Irish Traveller",
    isHeader: false,
  },
  {
    value: "other-white",
    label: "Any other White background",
    isHeader: false,
  },

  // Mixed or Multiple ethnic groups
  {
    value: "mixed-header",
    label: "Mixed or Multiple ethnic groups",
    isHeader: true,
  },
  {
    value: "white-black-caribbean",
    label: "White and Black Caribbean",
    isHeader: false,
  },
  {
    value: "white-black-african",
    label: "White and Black African",
    isHeader: false,
  },
  { value: "white-asian", label: "White and Asian", isHeader: false },
  {
    value: "other-mixed",
    label: "Any other Mixed or Multiple ethnic backgrounds",
    isHeader: false,
  },

  // Asian or Asian British backgrounds
  { value: "asian-header", label: "Asian or Asian British", isHeader: true },
  { value: "indian", label: "Indian", isHeader: false },
  { value: "pakistani", label: "Pakistani", isHeader: false },
  { value: "bangladeshi", label: "Bangladeshi", isHeader: false },
  { value: "chinese", label: "Chinese", isHeader: false },
  {
    value: "other-asian",
    label: "Any other Asian background",
    isHeader: false,
  },

  // Black, African, Caribbean or Black British
  {
    value: "black-header",
    label: "Black, African, Caribbean or Black British",
    isHeader: true,
  },
  { value: "african", label: "African", isHeader: false },
  { value: "caribbean", label: "Caribbean", isHeader: false },
  {
    value: "other-black",
    label: "Any other Black, African or Caribbean background",
    isHeader: false,
  },

  // Other ethnic groups
  { value: "other-header", label: "Other ethnic groups", isHeader: true },
  { value: "arab", label: "Arab", isHeader: false },
  { value: "any-other", label: "Any other ethnic group", isHeader: false },
];

// Care Leaver Options
const CARE_LEAVER_OPTIONS = [
  "Select Care Leaver Status",
  "Care leaver (16+)",
  "In care in the rest of UK",
  "Information refused",
  "Looked after in Scotland",
  "Not a care leaver",
  "Not known",
  "UCAS defined care leaver"
];

// List of UK country variations
const UK_COUNTRY_VARIANTS = [
  'uk',
  'united kingdom',
  'great britain',
  'britain',
  'england',
  'scotland',
  'wales',
  'northern ireland',
  'gb',
  'u.k.',
  'united kingdom of great britain and northern ireland'
];

const RegisterPage2 = ({ onSubmit, onPrevious }) => {
  const navigate = useNavigate();
  const LOCAL_KEY = "register_step2";
  const REGISTER_1_KEY = "register_step1";

  const [countries, setCountries] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showVisaRejection, setShowVisaRejection] = useState(false);
  const [showDateOfEntryUK, setShowDateOfEntryUK] = useState(false);

  // Check if country is UK (case insensitive and handles common variations)
  const isUKCountry = (country) => {
    if (!country) return false;
    return UK_COUNTRY_VARIANTS.includes(country.toLowerCase().trim());
  };

  // Check residency from register_1 localStorage
  useEffect(() => {
    const register1Data = localStorage.getItem(REGISTER_1_KEY);
    if (register1Data) {
      try {
        const parsedData = JSON.parse(register1Data);
        if (parsedData.residency === "31") {
          setShowVisaRejection(true);
        }
      } catch (error) {
        console.error("Error parsing register_1 data:", error);
      }
    }
  }, []);

  // Form state initialization
  const [formData, setFormData] = useState(() => {
    const saved = localStorage.getItem(LOCAL_KEY);
    return saved
      ? JSON.parse(saved)
      : {
          title: "",
          firstName: "",
          lastName: "",
          email: "",
          mobileNo: "",
          nationality: "",
          countryOfBirth: "",
          ethnicOrigin: "",
          dob: "",
          gender: "",
          highestQualification: "",
          lastInstitute: "",
          passportNo: "",
          passportExpiry: "",
          passportPlaceOfIssuance: "",
          visaRejected: "",
          maritalStatus: "",
          careLeaver: "", // Changed to string for dropdown
          dateOfEntryUK: "",
          corrPostalCode: "",
          corrAddressLine1: "",
          corrAddressLine2: "",
          corrAddressLine3: "",
          corrCity: "",
          corrCountry: "",
          corrPhoneNo: "",
          sameAsCorrespondence: false,
          permPostalCode: "",
          permAddressLine1: "",
          permAddressLine2: "",
          permAddressLine3: "",
          permCity: "",
          permCountry: "",
          permPhoneNo: "",
          nextOfKinName: "",
          nextOfKinRelation: "",
          nextOfKinPhoneNo: "",
          nextOfKinEmail: "",
        };
  });

  // Fetch countries from backend
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/countries`
        );
        if (!res.ok) {
          throw new Error(`HTTP ${res.status} - ${res.statusText}`);
        }
        const data = await res.json();
        setCountries(data);
      } catch (err) {
        console.error("Failed to fetch countries:", err);
      }
    };

    fetchCountries();
  }, []);

  // Save form data to localStorage
  useEffect(() => {
    localStorage.setItem(LOCAL_KEY, JSON.stringify(formData));
  }, [formData]);

  // Check if country of birth is UK and show/hide date of entry field
  useEffect(() => {
    const isUK = isUKCountry(formData.countryOfBirth);
    setShowDateOfEntryUK(!isUK);
    
    // If country of birth is UK, clear the date of entry field
    if (isUK) {
      setFormData(prev => ({
        ...prev,
        dateOfEntryUK: ""
      }));
    }
  }, [formData.countryOfBirth]);

  // Copy correspondence address to permanent address
  useEffect(() => {
    if (formData.sameAsCorrespondence) {
      setFormData((prev) => ({
        ...prev,
        permPostalCode: prev.corrPostalCode,
        permAddressLine1: prev.corrAddressLine1,
        permAddressLine2: prev.corrAddressLine2,
        permAddressLine3: prev.corrAddressLine3,
        permCity: prev.corrCity,
        permCountry: prev.corrCountry,
        permPhoneNo: prev.corrPhoneNo,
      }));
    }
  }, [
    formData.sameAsCorrespondence,
    formData.corrPostalCode,
    formData.corrAddressLine1,
    formData.corrAddressLine2,
    formData.corrAddressLine3,
    formData.corrCity,
    formData.corrCountry,
    formData.corrPhoneNo,
  ]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (name === "sameAsCorrespondence" && !checked) {
      setFormData((prev) => ({
        ...prev,
        permPostalCode: "",
        permAddressLine1: "",
        permAddressLine2: "",
        permAddressLine3: "",
        permCity: "",
        permCountry: "",
        permPhoneNo: "",
      }));
    }
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate API call
    setTimeout(() => {
      onSubmit(formData);
      setIsSubmitting(false);
    }, 1500);
  };

  // Personal information fields array
  const getPersonalInfoFields = () => {
    const baseFields = [
      {
        label: "Title *",
        name: "title",
        type: "select",
        options: ["Select Title", "Mr", "Ms", "Mrs", "Dr", "Prof"],
        icon: <FaUser className="text-gray-400 text-xs" />,
        required: true
      },
      {
        label: "First Name *",
        name: "firstName",
        placeholder: "Enter first name",
        icon: <FaUser className="text-gray-400 text-xs" />,
        required: true
      },
      {
        label: "Last Name *",
        name: "lastName",
        placeholder: "Enter last name",
        icon: <FaUser className="text-gray-400 text-xs" />,
        required: true
      },
      {
        label: "E-mail Address *",
        name: "email",
        type: "email",
        placeholder: "Enter email",
        icon: <FaEnvelope className="text-gray-400 text-xs" />,
        required: true
      },
      {
        label: "Mobile No *",
        name: "mobileNo",
        placeholder: "Enter mobile number",
        icon: <FaPhone className="text-gray-400 text-xs" />,
        required: true
      },
      {
        label: "Nationality *",
        name: "nationality",
        placeholder: "Enter nationality",
        icon: <FaGlobe className="text-gray-400 text-xs" />,
        required: true
      },
      {
        label: "Country of Birth *",
        name: "countryOfBirth",
        type: "select",
        options: ["Select Country", ...countries.map((c) => c.display_name)],
        icon: <FaMapMarkerAlt className="text-gray-400 text-xs" />,
        required: true
      },
      {
        label: "Ethnic Origin *",
        name: "ethnicOrigin",
        type: "ethnicSelect", // Special type for ethnic origin
        options: ETHNIC_ORIGIN_OPTIONS,
        icon: <FaGlobe className="text-gray-400 text-xs" />,
        required: true
      },
      {
        label: "Date of Birth *",
        name: "dob",
        type: "date",
        icon: <FaBirthdayCake className="text-gray-400 text-xs" />,
        required: true
      },
      {
        label: "Gender *",
        name: "gender",
        type: "select",
        options: ["Select Gender", "Male", "Female", "Other"],
        icon: <FaUser className="text-gray-400 text-xs" />,
        required: true
      },
      {
        label: "Highest Qualification *",
        name: "highestQualification",
        placeholder: "Bachelor, Master, etc.",
        icon: <FaGraduationCap className="text-gray-400 text-xs" />,
        required: true
      },
      {
        label: "Last Institute *",
        name: "lastInstitute",
        placeholder: "Enter last institute",
        icon: <FaGraduationCap className="text-gray-400 text-xs" />,
        required: true
      },
      {
        label: "Passport No *",
        name: "passportNo",
        placeholder: "Enter passport number",
        icon: <FaPassport className="text-gray-400 text-xs" />,
        required: true
      },
      {
        label: "Passport Expiry *",
        name: "passportExpiry",
        type: "date",
        icon: <FaPassport className="text-gray-400 text-xs" />,
        required: true
      },
      {
        label: "Passport Place of Issuance *",
        name: "passportPlaceOfIssuance",
        placeholder: "Enter place of issuance",
        icon: <FaPassport className="text-gray-400 text-xs" />,
        required: true
      },
    ];

    // Add visa rejection field only if residency is 31
    if (showVisaRejection) {
      baseFields.push({
        label: "Has Your Visa been Rejected *",
        name: "visaRejected",
        type: "select",
        options: ["Select Option", "Yes", "No"],
        icon: <FaPassport className="text-gray-400 text-xs" />,
        required: true
      });
    }

    // Add date of entry to UK field if country of birth is not UK
    if (showDateOfEntryUK) {
      baseFields.push({
        label: "Date of Entry to UK *",
        name: "dateOfEntryUK",
        type: "date",
        icon: <FaCalendarAlt className="text-gray-400 text-xs" />,
        required: true
      });
    }

    // Add marital status field
    baseFields.push({
      label: "Marital Status *",
      name: "maritalStatus",
      type: "select",
      options: [
        "Select Status",
        "Single",
        "Married",
        "Divorced",
        "Widowed",
        "Separated",
      ],
      icon: <FaHeart className="text-gray-400 text-xs" />,
      required: true
    });

    return baseFields;
  };

  return (
    <div className="min-h-screen flex flex-col items-center py-4 px-3 sm:px-4 lg:px-6">
      <div className="w-full max-w-6xl bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-cyan-600 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-center sm:text-left">
              <h1 className="text-xl sm:text-2xl font-bold text-white">
                Personal & Address Information
              </h1>
            </div>

            {/* Show button only if token does NOT exist */}
            {!localStorage.getItem("token") && (
              <button
                onClick={() => {
                  localStorage.clear();
                  navigate("/");
                }}
                className="flex items-center justify-center space-x-2 px-4 py-2 rounded-lg bg-white text-blue-700 font-semibold hover:bg-blue-50 hover:shadow transition-all duration-300 text-xs whitespace-nowrap self-center sm:self-auto"
              >
                <span>Already registered?</span>
                <span className="font-bold">Login</span>
              </button>
            )}
          </div>
        </div>

        <div className="overflow-y-auto max-h-[75vh] p-4 sm:p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Information Section */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center mb-4">
                <div className="bg-blue-100 p-1.5 rounded-md mr-3">
                  <FaUser className="text-blue-700 text-sm" />
                </div>
                <h3 className="text-lg font-bold text-gray-800">
                  Personal Information
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {getPersonalInfoFields().map((field, index) => (
                  <div key={index} className="relative">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {field.label}
                    </label>
                    <div className="relative">
                      {field.icon && (
                        <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                          {field.icon}
                        </div>
                      )}
                      {field.type === "ethnicSelect" ? (
                        <select
                          name={field.name}
                          value={formData[field.name]}
                          onChange={handleChange}
                          required={field.required}
                          className={`w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                            field.icon ? "pl-9" : ""
                          }`}
                        >
                          {ETHNIC_ORIGIN_OPTIONS.map((option, i) => (
                            <option
                              key={i}
                              value={option.isHeader ? "" : option.value}
                              disabled={option.isHeader}
                              className={
                                option.isHeader
                                  ? "font-bold bg-gray-100 text-gray-700"
                                  : ""
                              }
                              style={
                                option.isHeader
                                  ? {
                                      fontWeight: "bold",
                                      backgroundColor: "#f3f4f6",
                                    }
                                  : {}
                              }
                            >
                              {option.isHeader
                                ? `--- ${option.label} ---`
                                : option.label}
                            </option>
                          ))}
                        </select>
                      ) : field.type === "select" ? (
                        <select
                          name={field.name}
                          value={formData[field.name]}
                          onChange={handleChange}
                          required={field.required}
                          className={`w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                            field.icon ? "pl-9" : ""
                          }`}
                        >
                          {field.options.map((option, i) => {
                            const isDefaultOption = option.startsWith("Select");
                            return (
                              <option
                                key={i}
                                value={
                                  isDefaultOption ? "" : option.toLowerCase()
                                }
                              >
                                {option}
                              </option>
                            );
                          })}
                        </select>
                      ) : (
                        <input
                          type={field.type || "text"}
                          name={field.name}
                          placeholder={field.placeholder}
                          value={formData[field.name]}
                          onChange={handleChange}
                          required={field.required}
                          className={`w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                            field.icon ? "pl-9" : ""
                          }`}
                        />
                      )}
                    </div>
                  </div>
                ))}

                {/* Care Leaver - Updated to Dropdown */}
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Care Leaver Status *
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                      <FaUserFriends className="text-gray-400 text-xs" />
                    </div>
                    <select
                      name="careLeaver"
                      value={formData.careLeaver}
                      onChange={handleChange}
                      required
                      className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white"
                    >
                      {CARE_LEAVER_OPTIONS.map((option, index) => (
                        <option
                          key={index}
                          value={option === "Select Care Leaver Status" ? "" : option}
                        >
                          {option}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      <FaChevronDown className="text-gray-400 text-xs" />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Please select your care leaver status as defined by UCAS
                  </p>
                </div>
              </div>
            </div>

            {/* Correspondence Address Section */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center mb-4">
                <div className="bg-blue-100 p-1.5 rounded-md mr-3">
                  <FaHome className="text-blue-700 text-sm" />
                </div>
                <h3 className="text-lg font-bold text-gray-800">
                  Correspondence Address
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  {
                    label: "Postal Code *",
                    name: "corrPostalCode",
                    placeholder: "Enter postal code",
                    required: true
                  },
                  {
                    label: "Address Line 1 *",
                    name: "corrAddressLine1",
                    placeholder: "Enter address line 1",
                    required: true
                  },
                  {
                    label: "Address Line 2 *",
                    name: "corrAddressLine2",
                    placeholder: "Enter address line 2",
                    required: true
                  },
                  {
                    label: "Address Line 3 *",
                    name: "corrAddressLine3",
                    placeholder: "Enter address line 3",
                    required: true
                  },
                  {
                    label: "City *",
                    name: "corrCity",
                    placeholder: "Enter city",
                    required: true
                  },
                  {
                    label: "Country *",
                    name: "corrCountry",
                    type: "select",
                    options: [
                      "Select Country",
                      ...countries.map((c) => c.display_name),
                    ],
                    required: true
                  },
                  {
                    label: "Phone No *",
                    name: "corrPhoneNo",
                    placeholder: "Enter phone number",
                    required: true
                  },
                ].map((field, index) => (
                  <div key={index}>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {field.label}
                    </label>
                    {field.type === "select" ? (
                      <select
                        name={field.name}
                        value={formData[field.name]}
                        onChange={handleChange}
                        required={field.required}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      >
                        {field.options.map((option, i) => (
                          <option
                            key={i}
                            value={
                              option === "Select Country"
                                ? ""
                                : countries.find(
                                    (c) => c.display_name === option
                                  )?.id || ""
                            }
                          >
                            {option}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        name={field.name}
                        placeholder={field.placeholder}
                        value={formData[field.name]}
                        onChange={handleChange}
                        required={field.required}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    )}
                  </div>
                ))}

                <div className="md:col-span-2 flex items-center">
                  <label className="flex items-center cursor-pointer">
                    <div className="relative">
                      <input
                        type="checkbox"
                        name="sameAsCorrespondence"
                        checked={formData.sameAsCorrespondence}
                        onChange={handleChange}
                        className="sr-only"
                      />
                      <div
                        className={`w-8 h-4 rounded-full ${
                          formData.sameAsCorrespondence
                            ? "bg-blue-600"
                            : "bg-gray-300"
                        } transition-colors duration-300 ease-in-out`}
                      >
                        <div
                          className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full transition-transform duration-300 ease-in-out ${
                            formData.sameAsCorrespondence
                              ? "transform translate-x-4"
                              : ""
                          }`}
                        ></div>
                      </div>
                    </div>
                    <span className="ml-2 text-xs text-gray-700 font-medium">
                      Same as Correspondence Address
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Permanent Address Section */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center mb-4">
                <div className="bg-blue-100 p-1.5 rounded-md mr-3">
                  <FaHome className="text-blue-700 text-sm" />
                </div>
                <h3 className="text-lg font-bold text-gray-800">
                  Permanent Address
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  {
                    label: "Postal Code *",
                    name: "permPostalCode",
                    placeholder: "Enter postal code",
                    required: true
                  },
                  {
                    label: "Address Line 1 *",
                    name: "permAddressLine1",
                    placeholder: "Enter address line 1",
                    required: true
                  },
                  {
                    label: "Address Line 2 *",
                    name: "permAddressLine2",
                    placeholder: "Enter address line 2",
                    required: true
                  },
                  {
                    label: "Address Line 3 *",
                    name: "permAddressLine3",
                    placeholder: "Enter address line 3",
                    required: true
                  },
                  {
                    label: "City *",
                    name: "permCity",
                    placeholder: "Enter city",
                    required: true
                  },
                  {
                    label: "Country *",
                    name: "permCountry",
                    type: "select",
                    options: [
                      "Select Country",
                      ...countries.map((c) => c.display_name),
                    ],
                    required: true
                  },
                  {
                    label: "Phone No *",
                    name: "permPhoneNo",
                    placeholder: "Enter phone number",
                    required: true
                  },
                ].map((field, index) => (
                  <div key={index}>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {field.label}
                    </label>
                    {field.type === "select" ? (
                      <select
                        name={field.name}
                        value={formData[field.name]}
                        onChange={handleChange}
                        disabled={formData.sameAsCorrespondence}
                        required={field.required}
                        className={`w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                          formData.sameAsCorrespondence
                            ? "bg-gray-100 text-gray-500"
                            : ""
                        }`}
                      >
                        {field.options.map((option, i) => (
                          <option
                            key={i}
                            value={
                              option === "Select Country"
                                ? ""
                                : countries.find(
                                    (c) => c.display_name === option
                                  )?.id || ""
                            }
                          >
                            {option}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        name={field.name}
                        placeholder={field.placeholder}
                        value={formData[field.name]}
                        onChange={handleChange}
                        disabled={formData.sameAsCorrespondence}
                        required={field.required}
                        className={`w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                          formData.sameAsCorrespondence
                            ? "bg-gray-100 text-gray-500"
                            : ""
                        }`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Next of Kin Section */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center mb-4">
                <div className="bg-blue-100 p-1.5 rounded-md mr-3">
                  <FaUserFriends className="text-blue-700 text-sm" />
                </div>
                <h3 className="text-lg font-bold text-gray-800">Next of Kin</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  {
                    label: "Name *",
                    name: "nextOfKinName",
                    placeholder: "Enter full name",
                    icon: <FaUser className="text-gray-400 text-xs" />,
                    required: true
                  },
                  {
                    label: "Relation *",
                    name: "nextOfKinRelation",
                    placeholder: "Enter relationship",
                    icon: <FaUserFriends className="text-gray-400 text-xs" />,
                    required: true
                  },
                  {
                    label: "Phone No *",
                    name: "nextOfKinPhoneNo",
                    placeholder: "Enter phone number",
                    icon: <FaPhone className="text-gray-400 text-xs" />,
                    required: true
                  },
                  {
                    label: "Email *",
                    name: "nextOfKinEmail",
                    type: "email",
                    placeholder: "Enter email",
                    icon: <FaEnvelope className="text-gray-400 text-xs" />,
                    required: true
                  },
                ].map((field, index) => (
                  <div key={index} className="relative">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {field.label}
                    </label>
                    <div className="relative">
                      {field.icon && (
                        <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                          {field.icon}
                        </div>
                      )}
                      <input
                        type={field.type || "text"}
                        name={field.name}
                        placeholder={field.placeholder}
                        value={formData[field.name]}
                        onChange={handleChange}
                        required={field.required}
                        className={`w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                          field.icon ? "pl-9" : ""
                        }`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex flex-col sm:flex-row justify-between gap-3 pt-4">
              <button
                type="button"
                onClick={onPrevious}
                className="px-5 py-2.5 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors duration-300 flex-1 text-sm"
              >
                Previous
              </button>

              <button
                type="submit"
                disabled={isSubmitting}
                className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-lg font-medium text-white hover:opacity-90 transition-opacity duration-300 flex-1 disabled:opacity-70 flex items-center justify-center text-sm"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center space-x-2">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span>Processing...</span>
                  </span>
                ) : (
                  "Next"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage2;